import { useEffect, useState } from "react";
import { ResponsiveContainer, ComposedChart, Bar, Line, XAxis, YAxis, Tooltip, CartesianGrid } from "recharts";
import { Users, Store, ClipboardList, Wallet, Percent, Coins, Banknote, AlertTriangle } from "lucide-react";
import { api, rupiah } from "@/lib/api";
import { AdminHead } from "@/components/admin/AdminUI";
import { StatusBadge } from "@/components/StatusBadge";
import { PageLoader } from "@/components/layout/RequireAuth";

const Kpi = ({ icon: Icon, label, value, id }) => (
  <div className="card-soft p-5" data-testid={`kpi-${id}`}>
    <Icon className="h-5 w-5 text-teal-700" />
    <p className="mt-3 text-xs text-stone-500">{label}</p>
    <p className="text-xl font-extrabold text-stone-900" data-testid={`kpi-${id}-value`}>{value}</p>
  </div>
);

export default function AdminDashboard() {
  const [d, setD] = useState(null);
  useEffect(() => { api.get("/admin/dashboard").then((r) => setD(r.data)); }, []);
  if (!d) return <PageLoader />;
  const series = d.series.map((s) => ({ ...s, label: s.date.slice(8) + "/" + s.date.slice(5, 7) }));

  return (
    <div data-testid="admin-dashboard">
      <AdminHead title="Dasbor" desc="Ringkasan aktivitas marketplace Tuntas." />
      <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
        <Kpi icon={Users} id="customers" label="Pelanggan" value={d.users.pelanggan} />
        <Kpi icon={Store} id="providers" label="Penyedia" value={d.users.penyedia} />
        <Kpi icon={ClipboardList} id="active-requests" label="Permintaan aktif" value={d.active_requests} />
        <Kpi icon={AlertTriangle} id="complaints" label="Komplain terbuka" value={d.open_complaints} />
        <Kpi icon={Wallet} id="gmv" label="GMV (dibayar)" value={rupiah(d.gmv)} />
        <Kpi icon={Percent} id="commission" label="Total komisi" value={rupiah(d.commission)} />
        <Kpi icon={Coins} id="credit-sales" label="Penjualan kredit" value={rupiah(d.credit_sales)} />
        <Kpi icon={Banknote} id="pending-payouts" label="Siap dicairkan" value={rupiah(d.pending_payouts)} />
      </div>
      <div className="card-soft mt-6 p-6" data-testid="orders-by-status">
        <p className="mb-4 text-sm font-bold text-stone-900">Pesanan per status</p>
        <div className="flex flex-wrap gap-3">
          {Object.entries(d.orders_by_status).map(([s, n]) => (
            <span key={s} className="flex items-center gap-2"><StatusBadge status={s} testId={`dash-status-${s}`} /><b className="text-stone-900">{n}</b></span>
          ))}
        </div>
      </div>
      <div className="card-soft mt-6 p-6" data-testid="dashboard-chart">
        <p className="mb-4 text-sm font-bold text-stone-900">Pesanan & GMV 30 hari terakhir</p>
        <div className="h-72">
          <ResponsiveContainer width="100%" height="100%">
            <ComposedChart data={series}>
              <CartesianGrid stroke="#f0eeec" vertical={false} />
              <XAxis dataKey="label" tick={{ fontSize: 11, fill: "#78716c" }} interval={3} />
              <YAxis yAxisId="o" allowDecimals={false} tick={{ fontSize: 11, fill: "#78716c" }} />
              <YAxis yAxisId="g" orientation="right" tick={{ fontSize: 11, fill: "#78716c" }} tickFormatter={(v) => `${Math.round(v / 1e6)} jt`} />
              <Tooltip formatter={(v, n) => (n === "GMV" ? rupiah(v) : v)} />
              <Bar yAxisId="o" dataKey="orders" name="Pesanan" fill="#99d5cf" radius={[4, 4, 0, 0]} />
              <Line yAxisId="g" dataKey="gmv" name="GMV" stroke="#0f766e" strokeWidth={2} dot={false} />
            </ComposedChart>
          </ResponsiveContainer>
        </div>
      </div>
    </div>
  );
}
